import { motion } from 'framer-motion';
import { Users, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useRef, useState, useEffect } from 'react';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';


const creationDeskData = {
  badge: {
    text: "Optional",
    variant: "secondary" as const
  },
  icon: Users,
  iconColor: "bg-blue-500",
  heading: "Add Creation Desk",
  subheadline: "Your on-demand creative team",
  tagline: "Sometimes even the best marketers get swamped with work. From brief to final asset, faster and better, by the people who know the platform inside-out.",
  features: ["Expert gimmefy users working inside your workspace", "Brief → Create → QA → Deliver", "High quality assets out the door faster when things are busy", "Dial hours up and down as needed"],
  buttonText: "See Creation Desk",
  buttonLink: "/enterprise#creation-desk"
};

const CreationDeskSection = () => {
  const videoRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    if (videoRef.current) {
      observer.observe(videoRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative py-16 sm:py-20 lg:py-24 bg-background overflow-hidden">
      {/* Floating accent shapes */}
      <motion.div
        className="absolute top-1/4 -left-10 w-32 h-32 bg-blue-400/10 rounded-full blur-2xl hidden lg:block"
        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3], x: [0, 10, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-1/4 -right-10 w-40 h-40 bg-purple-400/10 rounded-full blur-2xl hidden lg:block"
        animate={{ scale: [1, 1.15, 1], opacity: [0.25, 0.45, 0.25], x: [0, -10, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true }} 
          transition={{ duration: 0.5 }} 
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center max-w-6xl mx-auto"
        >
          {/* Left: Video with glow */}
          <div className="relative order-2 lg:order-1">
            {/* Glow pulse around video */}
            <motion.div
              className="absolute -inset-2 bg-gradient-to-r from-blue-400/20 via-purple-400/20 to-blue-400/20 rounded-2xl blur-xl"
              animate={{ opacity: [0.3, 0.5, 0.3] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            />
            <div ref={videoRef} className="relative rounded-2xl overflow-hidden shadow-xl border border-border/50">
              <AspectRatio ratio={16 / 9}>
                {isInView ? (
                  <iframe
                    src="https://www.youtube.com/embed/BjwWJyJMvKo?si=bFFqPQr16LRLJ8tV"
                    title="Creation Desk Demo Video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-muted to-muted/50 flex items-center justify-center">
                    <Play className="w-16 h-16 text-muted-foreground/50" />
                  </div>
                )}
              </AspectRatio>
            </div>
          </div>

          {/* Right: Content */}
          <div className="text-center lg:text-left order-1 lg:order-2">
            <Badge variant="secondary" className="w-fit mb-4 mx-auto lg:mx-0">
              {creationDeskData.badge?.text}
            </Badge>

            {/* Header with icon always on left of title */}
            <div className="flex items-start gap-3 sm:gap-4 mb-4 justify-center lg:justify-start">
              <div className={`w-12 h-12 ${creationDeskData.iconColor} rounded-xl flex items-center justify-center flex-shrink-0`}>
                <Users className="h-6 w-6 text-white" />
              </div>
              <div className="text-left">
                <h3 className="text-2xl lg:text-3xl font-bold text-foreground">
                  {creationDeskData.heading}
                </h3>
                <p className="text-base lg:text-lg text-secondary-foreground mt-1">
                  {creationDeskData.subheadline}
                </p>
              </div>
            </div>

            {/* Tagline */}
            <p className="mb-6 text-secondary-foreground text-base lg:text-lg text-center lg:text-left">
              {creationDeskData.tagline}
            </p>

            {/* Feature Pills with staggered entrance */}
            <div className="flex flex-wrap gap-2 mb-8 justify-center lg:justify-start">
              {creationDeskData.features.map((feature, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: i * 0.1 }}
                  className="px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium"
                >
                  {feature}
                </motion.span>
              ))}
            </div>

            {/* CTA */}
            <div className="flex justify-center lg:justify-start">
              <Button variant="default" asChild>
                <Link to={creationDeskData.buttonLink}>{creationDeskData.buttonText}</Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CreationDeskSection;
